import type { ItemOffer, OptionGroup } from "@/types";
import { roundCurrency } from "./compute-order-total";
import { priceGroupTotal } from "./price-group-total";

/** groupId -> (optionId -> quantity). Mirrors itemOptionsQuantitiesAtom / CartItem.optionQuantities. */
export type SelectionQuantities = Record<string, Record<string, number>>;

/**
 * Clamp a money input to a finite, non-negative number. Boundary guard against
 * malformed API data (negative promo prices, NaN-coerced missing fields) so a
 * bad value can never seed a negative price.
 */
export function sanitizeMoney(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Single source of truth for an item's unit price: the (clamped) base offer
 * price plus the priced contribution of every option group for the current
 * selection, rounded to 2 decimals PER UNIT (round-por-unidade) and never
 * negative. The unit price is a real money value that is displayed and then
 * multiplied by quantity, so it must round here — mirroring order-service-core
 * CalculateItemPrice — so the displayed unit price always closes with the line
 * (e.g. base 4,655 + 2,00 → 6,66; 2 un → 6,66 × 2 = 13,32) and the cart total
 * never drifts from the core. Replaces the scattered imperative `+/-` deltas
 * that used to mutate the old `itemTotalPriceAtom` accumulator.
 */
export function computeItemTotal(
  itemOffer: Pick<ItemOffer, "price"> | null | undefined,
  optionGroups: OptionGroup[] | null | undefined,
  quantities: SelectionQuantities | null | undefined,
): number {
  const base = sanitizeMoney(itemOffer?.price?.value);
  const groups = optionGroups ?? [];
  const selection = quantities ?? {};

  const optionsTotal = groups.reduce(
    (sum, group) => sum + priceGroupTotal(group, selection[group.id] ?? {}),
    0,
  );

  const total = base + optionsTotal;
  return total > 0 ? roundCurrency(total) : 0;
}
